import type { CustomFlowbiteTheme } from "flowbite-react";

// Comprehensive Color System for Facility Admin Frontend
export const colors = {
  // Primary Colors
  primary: "#2A333C",
  primaryLight: "#677D94", 
  primaryDark: "#1A2128",
  
  // Secondary Colors
  secondary: "#F5B957",
  secondaryLight: "#FCECD1",
  secondaryDark: "#AB6D0A",
  
  // Contextual Colors
  conPrimary: "#1F2937",
  iconSecondary: "#7A6646",
  iconTertiary: "#94A3B8",
  iconFourth: "#CBD5E1",
  iconInverted: "#FFFFFF",
  
  // Accent Colors
  badgePrimary: "#FF6B6B",
  
  // Green Palette
  green50: "#F0F9F0",
  green100: "#E0EDE2",
  green200: "#C3DBC6",
  green300: "#A6C9AA",
  green400: "#89B78E",
  green500: "#6CA572",
  green600: "#3A7D44",
  green700: "#2E6B3A",
  green800: "#225930",
  green900: "#164726",
  
  // Blue Palette
  blue50: "#F0F4F8",
  blue100: "#E0E8ED",
  blue200: "#C1D1DB",
  blue300: "#A2BAC9",
  blue400: "#83A3B7",
  blue500: "#4A6FA5",
  blue600: "#3A5F7D",
  blue700: "#3A5F7D",
  blue800: "#2A4A6C",
  blue900: "#1A334C",
  
  // Yellow Palette
  yellow50: "#FFFBEB",
  yellow100: "#FFF8E6",
  yellow200: "#FEF3C7",
  yellow300: "#FDE68A",
  yellow400: "#FCD34D",
  yellow500: "#F59E0B",
  yellow600: "#D97706",
  yellow700: "#B45309",
  yellow800: "#D4A123",
  yellow900: "#78350F",
  
  // Red Palette
  red50: "#FEF2F2",
  red100: "#FFEAEA",
  red200: "#FECACA",
  red300: "#FCA5A5",
  red400: "#F87171",
  red500: "#C84648",
  red600: "#DC2626",
  red700: "#A23537",
  red800: "#991B1B",
  red900: "#7A2527",
  
  // Orange Palette
  orange50: "#FFF5EB",
  orange100: "#FCE8D9",
  orange200: "#F9D5A3",
  orange300: "#F6C26D",
  orange400: "#F3AF37",
  orange500: "#E67E34",
  orange600: "#D97706",
  orange700: "#C4450D",
  orange800: "#BF5A1F",
  orange900: "#92400E",
  
  // Neutral Colors
  gray50: "#F9FAFB",
  gray100: "#F3F4F6",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#9CA3AF",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",
  
  // Card & Background Colors
  card1: "#F8FAFC",
  card2: "#FFFFFF",
  card3: "#F1F5F9",
  
  // Status Colors
  success: "#3A7D44",
  warning: "#D4A123",
  error: "#C84648",
  info: "#4A6FA5",
  
  // Text Colors
  textPrimary: "#2A333C",
  textSecondary: "#677D94",
  textTertiary: "#94A3B8",
  textInverted: "#FFFFFF",
  
  // Border Colors
  borderLight: "#E0E8ED",
  borderMedium: "#CBD5E1",
  borderDark: "#94A3B8",
  
  // Shadow Colors
  shadowLight: "rgba(42, 51, 60, 0.05)",
  shadowMedium: "rgba(42, 51, 60, 0.1)",
  shadowDark: "rgba(42, 51, 60, 0.2)",
};

// Semantic color mappings
export const semanticColors = {
  // Backgrounds
  background: {
    primary: colors.card1,
    secondary: colors.card2,
    tertiary: colors.card3,
    dark: colors.primaryDark,
    gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
  },
  
  // Text
  text: {
    primary: colors.textPrimary,
    secondary: colors.textSecondary,
    tertiary: colors.textTertiary,
    inverted: colors.textInverted,
    muted: colors.iconTertiary,
  },
  
  // Buttons
  button: {
    primary: {
      background: colors.primary,
      text: colors.iconInverted,
      hover: colors.primaryDark,
    },
    secondary: {
      background: colors.secondary,
      text: colors.primary,
      hover: colors.secondaryDark,
    },
    success: {
      background: colors.green600,
      text: colors.iconInverted,
      hover: colors.green700,
    },
    danger: {
      background: colors.red500,
      text: colors.iconInverted,
      hover: colors.red700,
    },
    warning: {
      background: colors.yellow800,
      text: colors.iconInverted,
      hover: colors.yellow900,
    },
    info: {
      background: colors.blue500,
      text: colors.iconInverted,
      hover: colors.blue700,
    },
  },
  
  // Status
  status: {
    success: colors.green600,
    warning: colors.yellow800,
    error: colors.red500,
    info: colors.blue500,
    pending: colors.orange500,
  },
  
  // Cards
  card: {
    background: colors.card2,
    border: colors.borderLight,
    shadow: colors.shadowLight,
    hover: colors.shadowMedium,
  },
  
  // Borders
  border: {
    light: colors.borderLight,
    medium: colors.borderMedium,
    dark: colors.borderDark,
  },
  
  // Badges
  badge: {
    success: {
      background: colors.green100,
      text: colors.green600,
      border: colors.green600,
    },
    warning: {
      background: colors.yellow100,
      text: colors.yellow800,
      border: colors.yellow800,
    },
    error: {
      background: colors.red100,
      text: colors.red500,
      border: colors.red500,
    },
    info: {
      background: colors.blue100,
      text: colors.blue500,
      border: colors.blue500,
    },
    primary: {
      background: colors.badgePrimary,
      text: colors.iconInverted,
      border: colors.badgePrimary,
    },
  },
};

export default colors;